import React, { lazy, Suspense } from 'react';
import { Box, Center, Spinner } from '@chakra-ui/react';

/**
 * Creates a dynamically imported component with a loading state
 * This helps reduce initial bundle size by loading components only when needed
 */
export function lazyLoad(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  factory: () => Promise<{ default: React.ComponentType<any> }>,
  displayName = 'LazyComponent'
) {
  const LazyComponent = lazy(factory);
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const LoadableComponent = (props: any) => (
    <Suspense
      fallback={
        <Center py={8}>
          <Spinner size="xl" role="status" aria-label="Loading" />
        </Center>
      }
    >
      <LazyComponent {...props} />
    </Suspense>
  );

  LoadableComponent.displayName = `Lazy(${displayName})`;
  
  return LoadableComponent;
}

/**
 * Creates a route component that will be loaded dynamically with a full-page loading spinner
 * This optimization works especially well with React Router
 */
export function lazyLoadRoute(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  factory: () => Promise<{ default: React.ComponentType<any> }>,
  displayName = 'LazyRoute'
) {
  const LazyComponent = lazy(factory);
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const LoadableRoute = (props: any) => (
    <Suspense
      fallback={
        <Box 
          height="100%" 
          width="100%" 
          display="flex" 
          alignItems="center" 
          justifyContent="center"
        >
          <Spinner size="xl" role="status" aria-label="Loading route" />
        </Box>
      }
    >
      <LazyComponent {...props} />
    </Suspense>
  );

  LoadableRoute.displayName = `LazyRoute(${displayName})`;
  
  return LoadableRoute;
}

// Use these functions to create lazily loaded components instead of using React.lazy directly
// Example:
// const MyComponent = lazyLoad(() => import('./MyComponent'), 'MyComponent');
